/** A real tablist, so it is one tab stop and the arrow keys move between the tabs. */
import { useRef, type KeyboardEvent } from 'react';

import './list-page.css';

export interface FilterTabsProps<T extends string> {
  tabs: readonly T[];
  labels: Record<T, string>;
  counts: Record<T, number>;
  active: T;
  onChange: (next: T) => void;
  panelId: string;
  ariaLabel?: string;
}

export function FilterTabs<T extends string>({
  tabs,
  labels,
  counts,
  active,
  onChange,
  panelId,
  ariaLabel = 'Filter by status',
}: FilterTabsProps<T>) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const index = (tabs.indexOf(active) + step + tabs.length) % tabs.length;
    onChange(tabs[index]);
    tabRefs.current[index]?.focus();
  };

  return (
    <div
      className="nb-seg nb-listpage-filter-tabs"
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      style={{ flex: '0 0 auto' }}
    >
      {tabs.map((tab, i) => {
        const selected = tab === active;
        return (
          <button
            key={tab}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
          >
            {labels[tab]}
            <span
              className="nb-tabular"
              style={{ fontSize: 11, color: 'var(--nb-faint)', fontWeight: 700 }}
            >
              {counts[tab]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
